/**
 * Key Code Form — Shopify storefront integration
 * ================================================
 * Handles all 4 form types: key_code, standard, dropdown, multi_code.
 * Includes code range validation engine supporting 4 range formats:
 *   PURE_NUMERIC, FIXED_1LETTER, FIXED_2LETTER, VARIABLE_1LETTER
 */
(function () {
  'use strict';

  const form = document.getElementById('kcf-product-form');
  if (!form) return;

  const formType = form.dataset.formType || 'standard';
  const variantId = form.dataset.productId;
  const rangesStr = form.dataset.keyCodeRanges || '';
  const dropdownStr = form.dataset.dropdownOptions || '';
  const multiLabelsStr = form.dataset.multiCodeLabels || '';

  // ============================================================
  // CODE RANGE VALIDATION ENGINE
  // ============================================================

  /**
   * Parse a range string like "001-200, L99-L900, SP001-SP400"
   * into an array of range objects.
   */
  function parseRanges(str) {
    if (!str.trim()) return [];

    const ranges = [];
    // Split on commas (but not commas inside ranges)
    const parts = str.split(/,\s*/);

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      // Normalise separators: " to " or " - " or "-" -> " - "
      const normalised = trimmed
        .replace(/\s+to\s+/gi, ' - ')
        .replace(/\s*-\s*/, ' - ');

      const dashIdx = normalised.indexOf(' - ');
      if (dashIdx === -1) {
        // Single value, not a range — treat as exact match
        const decomposed = decomposeCode(trimmed);
        ranges.push({
          startPrefix: decomposed.prefix,
          startNum: decomposed.number,
          endPrefix: decomposed.prefix,
          endNum: decomposed.number,
          raw: trimmed,
        });
        continue;
      }

      const startStr = normalised.substring(0, dashIdx).trim();
      const endStr = normalised.substring(dashIdx + 3).trim();

      const start = decomposeCode(startStr);
      const end = decomposeCode(endStr);

      ranges.push({
        startPrefix: start.prefix,
        startNum: start.number,
        endPrefix: end.prefix,
        endNum: end.number,
        raw: trimmed,
      });
    }

    return ranges;
  }

  /**
   * Decompose a code like "SP001" into { prefix: "SP", number: 1 }
   * or "42000" into { prefix: "", number: 42000 }
   */
  function decomposeCode(code) {
    const match = code.match(/^([A-Za-z]*)(\d+)$/);
    if (match) {
      return {
        prefix: match[1].toUpperCase(),
        number: parseInt(match[2], 10),
      };
    }
    // If no match, return the whole thing as prefix with no number
    return { prefix: code.toUpperCase(), number: NaN };
  }

  /**
   * Validate a user-entered code against parsed ranges.
   * Returns { valid: true } or { valid: false, message: '...' }
   */
  function validateCode(code, ranges) {
    if (!code.trim()) {
      return { valid: false, message: 'Please enter a key code' };
    }

    if (ranges.length === 0) {
      // No ranges to validate against — accept anything
      return { valid: true };
    }

    const userCode = decomposeCode(code.trim());

    if (isNaN(userCode.number)) {
      return { valid: false, message: 'Invalid code format' };
    }

    for (const range of ranges) {
      if (matchesRange(userCode, range)) {
        return { valid: true };
      }
    }

    return {
      valid: false,
      message: 'Code not in valid range. Check the code ranges listed above.',
    };
  }

  /**
   * Check if a decomposed user code matches a single range.
   */
  function matchesRange(userCode, range) {
    const sp = range.startPrefix;
    const ep = range.endPrefix;
    const up = userCode.prefix;

    // PURE_NUMERIC: both prefixes empty
    if (sp === '' && ep === '') {
      if (up !== '') return false;
      return userCode.number >= range.startNum && userCode.number <= range.endNum;
    }

    // FIXED prefix: start prefix == end prefix
    if (sp === ep) {
      if (up !== sp) return false;
      return userCode.number >= range.startNum && userCode.number <= range.endNum;
    }

    // VARIABLE prefix: start prefix != end prefix (alphabetical range)
    if (up.length !== sp.length) return false;
    if (up < sp || up > ep) return false;

    // If prefix matches start, number must be >= start number
    if (up === sp) return userCode.number >= range.startNum;
    // If prefix matches end, number must be <= end number
    if (up === ep) return userCode.number <= range.endNum;
    // If prefix is between start and end, any number is valid
    return true;
  }

  const parsedRanges = parseRanges(rangesStr);

  // ============================================================
  // KEY PHOTO UPLOAD
  // ============================================================

  var photoBlob = null;
  var PHOTO_MAX_DIM = 2400;
  var PHOTO_QUALITY = 0.85;

  /** Yale-type cylinder key silhouette SVG (white stroke, no fill) */
  var KEY_SILHOUETTE_SVG =
    '<svg class="kcf-camera-overlay__svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 300" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="60" cy="50" r="35"/>' +
      '<circle cx="60" cy="50" r="12"/>' +
      '<line x1="60" y1="85" x2="60" y2="280"/>' +
      '<line x1="42" y1="85" x2="78" y2="85"/>' +
      '<polyline points="60,130 38,140 38,155 60,150"/>' +
      '<polyline points="60,160 32,172 32,187 60,180"/>' +
      '<polyline points="60,190 40,200 40,215 60,210"/>' +
      '<polyline points="60,230 35,242 35,257 60,250"/>' +
    '</svg>';

  /**
   * Wire up the photo trigger button, file input, and remove button.
   */
  function initPhotoSection() {
    var trigger = document.getElementById('kcf-photo-trigger');
    var fileInput = document.getElementById('kcf-photo-file-input');
    var removeBtn = document.getElementById('kcf-photo-remove');

    if (!trigger || !fileInput) return;

    trigger.addEventListener('click', function () {
      // Try camera first on devices that support it
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        openCameraModal();
      } else {
        fileInput.click();
      }
    });

    fileInput.addEventListener('change', function () {
      if (fileInput.files && fileInput.files[0]) {
        processSelectedFile(fileInput.files[0]);
      }
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        clearPhoto();
      });
    }
  }

  /**
   * Open a fullscreen camera modal with video stream + key silhouette overlay.
   */
  function openCameraModal() {
    var modal = document.createElement('div');
    modal.className = 'kcf-camera-modal';
    modal.id = 'kcf-camera-modal';

    modal.innerHTML =
      '<div class="kcf-camera-viewfinder">' +
        '<video class="kcf-camera-video" id="kcf-camera-video" autoplay playsinline muted></video>' +
        '<div class="kcf-camera-overlay">' + KEY_SILHOUETTE_SVG + '</div>' +
      '</div>' +
      '<div class="kcf-camera-controls" id="kcf-camera-controls">' +
        '<button type="button" class="kcf-camera-btn kcf-camera-btn--close" id="kcf-cam-close">Cancel</button>' +
        '<button type="button" class="kcf-camera-btn kcf-camera-btn--capture" id="kcf-cam-capture"></button>' +
      '</div>';

    document.body.appendChild(modal);

    var video = document.getElementById('kcf-camera-video');
    var captureBtn = document.getElementById('kcf-cam-capture');
    var closeBtn = document.getElementById('kcf-cam-close');
    var stream = null;

    function stopStream() {
      if (stream) {
        stream.getTracks().forEach(function (t) { t.stop(); });
        stream = null;
      }
    }

    function closeModal() {
      stopStream();
      modal.remove();
    }

    closeBtn.addEventListener('click', closeModal);

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then(function (s) {
        stream = s;
        video.srcObject = s;
      })
      .catch(function () {
        // Camera denied or not available — fall back to file picker
        closeModal();
        var fileInput = document.getElementById('kcf-photo-file-input');
        if (fileInput) fileInput.click();
      });

    captureBtn.addEventListener('click', function () {
      captureFrame(video, modal, stopStream);
    });
  }

  /**
   * Capture the current video frame to canvas (overlay is NOT captured)
   * and show retake/use preview inside the modal.
   */
  function captureFrame(video, modal, stopStream) {
    var canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    // Pause the stream to freeze the frame
    video.pause();

    // Create preview image from canvas
    var dataUrl = canvas.toDataURL('image/jpeg', PHOTO_QUALITY);

    // Replace viewfinder with captured preview (as flex sibling so controls stay visible)
    var viewfinder = modal.querySelector('.kcf-camera-viewfinder');
    var preview = document.createElement('div');
    preview.className = 'kcf-camera-preview';
    preview.innerHTML = '<img src="' + dataUrl + '" alt="Captured photo">';
    viewfinder.style.display = 'none';
    modal.insertBefore(preview, viewfinder.nextSibling);

    // Replace controls with retake / upload buttons
    var controls = document.getElementById('kcf-camera-controls');
    controls.innerHTML =
      '<button type="button" class="kcf-camera-btn kcf-camera-btn--retake" id="kcf-cam-retake">Retake</button>' +
      '<button type="button" class="kcf-camera-btn kcf-camera-btn--use" id="kcf-cam-use">Upload Photo</button>';

    document.getElementById('kcf-cam-retake').addEventListener('click', function () {
      // Remove preview, resume viewfinder
      preview.remove();
      viewfinder.style.display = '';
      video.play();
      // Restore capture controls
      controls.innerHTML =
        '<button type="button" class="kcf-camera-btn kcf-camera-btn--close" id="kcf-cam-close2">Cancel</button>' +
        '<button type="button" class="kcf-camera-btn kcf-camera-btn--capture" id="kcf-cam-capture2"></button>';
      document.getElementById('kcf-cam-close2').addEventListener('click', function () {
        stopStream();
        modal.remove();
      });
      document.getElementById('kcf-cam-capture2').addEventListener('click', function () {
        captureFrame(video, modal, stopStream);
      });
    });

    document.getElementById('kcf-cam-use').addEventListener('click', function () {
      resizeAndBlobify(canvas, function (blob) {
        photoBlob = blob;
        showFormPreview(dataUrl);
        stopStream();
        modal.remove();
      });
    });
  }

  /**
   * Downscale a canvas to max PHOTO_MAX_DIM longest edge, return JPEG blob.
   */
  function resizeAndBlobify(sourceCanvas, callback) {
    var w = sourceCanvas.width;
    var h = sourceCanvas.height;
    var scale = 1;

    if (w > PHOTO_MAX_DIM || h > PHOTO_MAX_DIM) {
      scale = PHOTO_MAX_DIM / Math.max(w, h);
    }

    var outW = Math.round(w * scale);
    var outH = Math.round(h * scale);

    var out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    var ctx = out.getContext('2d');
    ctx.drawImage(sourceCanvas, 0, 0, outW, outH);

    out.toBlob(function (blob) {
      callback(blob);
    }, 'image/jpeg', PHOTO_QUALITY);
  }

  /**
   * Handle file input fallback: load image, resize, preview.
   */
  function processSelectedFile(file) {
    var reader = new FileReader();
    reader.onload = function (e) {
      var img = new Image();
      img.onload = function () {
        var canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        resizeAndBlobify(canvas, function (blob) {
          photoBlob = blob;
          showFormPreview(e.target.result);
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  /**
   * Show the captured/selected photo as a thumbnail on the form.
   */
  function showFormPreview(dataUrl) {
    var previewEl = document.getElementById('kcf-photo-preview');
    var thumb = document.getElementById('kcf-photo-thumb');
    var trigger = document.getElementById('kcf-photo-trigger');

    if (thumb) thumb.src = dataUrl;
    if (previewEl) previewEl.classList.add('is-visible');
    if (trigger) trigger.style.display = 'none';
  }

  /**
   * Remove the photo and reset the UI.
   */
  function clearPhoto() {
    photoBlob = null;
    var previewEl = document.getElementById('kcf-photo-preview');
    var thumb = document.getElementById('kcf-photo-thumb');
    var trigger = document.getElementById('kcf-photo-trigger');
    var fileInput = document.getElementById('kcf-photo-file-input');

    if (previewEl) previewEl.classList.remove('is-visible');
    if (thumb) thumb.src = '';
    if (trigger) trigger.style.display = '';
    if (fileInput) fileInput.value = '';
  }

  // ============================================================
  // VOLUME PRICING TABLE
  // ============================================================

  function renderVolumePricing() {
    const tbody = document.getElementById('kcf-volume-rows');
    if (!tbody) return;

    try {
      // Volume pricing is stored as a JSON metafield
      const metaTag = form.closest('[data-volume-pricing]');
      let tiers = [];

      // Try to get from a script tag or data attribute
      const pricingScript = document.querySelector('[data-volume-pricing]');
      if (pricingScript) {
        tiers = JSON.parse(pricingScript.dataset.volumePricing);
      }

      if (tiers.length === 0) return;

      for (const tier of tiers) {
        const row = document.createElement('tr');
        row.innerHTML =
          '<td>' + tier.min_quantity + '+</td>' +
          '<td>&pound;' + parseFloat(tier.price).toFixed(2) + '</td>';
        tbody.appendChild(row);
      }
    } catch (e) {
      // Volume pricing not available or parse error
    }
  }

  // ============================================================
  // KEY CODE FORM (Type 1)
  // ============================================================

  function initKeyCodeForm() {
    const container = document.getElementById('kcf-rows-container');
    if (!container) return;

    let rowCount = 1;

    function addRow() {
      const idx = rowCount++;
      const row = document.createElement('div');
      row.className = 'kcf-row';
      row.dataset.rowIndex = idx;
      row.innerHTML =
        '<div class="kcf-row__fields">' +
          '<div class="kcf-field kcf-field--code">' +
            '<label class="kcf-field__label">Key Code</label>' +
            '<input type="text" class="kcf-input kcf-input--code" placeholder="Enter code" autocomplete="off">' +
            '<span class="kcf-field__error" style="display:none;"></span>' +
          '</div>' +
          '<div class="kcf-field kcf-field--qty">' +
            '<label class="kcf-field__label">Qty</label>' +
            '<input type="number" class="kcf-input kcf-input--qty" value="1" min="1">' +
          '</div>' +
          '<button type="button" class="kcf-row__remove" title="Remove row">&times;</button>' +
        '</div>';
      container.appendChild(row);
      attachRowEvents(row);
    }

    function attachRowEvents(row) {
      const codeInput = row.querySelector('.kcf-input--code');
      const removeBtn = row.querySelector('.kcf-row__remove');

      // Auto-add new row when focusing the last empty code input
      codeInput.addEventListener('focus', function () {
        const rows = container.querySelectorAll('.kcf-row');
        const lastRow = rows[rows.length - 1];
        if (row === lastRow) {
          addRow();
        }
      });

      // Validate on blur
      codeInput.addEventListener('blur', function () {
        const val = codeInput.value.trim();
        const errorEl = row.querySelector('.kcf-field__error');
        if (val) {
          const result = validateCode(val, parsedRanges);
          if (!result.valid) {
            errorEl.textContent = result.message;
            errorEl.style.display = '';
            codeInput.classList.add('kcf-input--error');
          } else {
            errorEl.style.display = 'none';
            codeInput.classList.remove('kcf-input--error');
          }
        } else {
          errorEl.style.display = 'none';
          codeInput.classList.remove('kcf-input--error');
        }
      });

      // Remove row
      removeBtn.addEventListener('click', function () {
        const rows = container.querySelectorAll('.kcf-row');
        if (rows.length > 1) {
          row.remove();
        }
      });

      // Show remove button on all rows except the first if there are > 1
      updateRemoveButtons();
    }

    function updateRemoveButtons() {
      const rows = container.querySelectorAll('.kcf-row');
      rows.forEach(function (r, i) {
        const btn = r.querySelector('.kcf-row__remove');
        if (btn) {
          btn.style.display = rows.length > 1 ? '' : 'none';
        }
      });
    }

    // Attach events to the initial row
    const firstRow = container.querySelector('.kcf-row');
    if (firstRow) attachRowEvents(firstRow);

    // Observe container for row changes to update remove buttons
    const observer = new MutationObserver(updateRemoveButtons);
    observer.observe(container, { childList: true });
  }

  function getKeyCodeCartData() {
    const container = document.getElementById('kcf-rows-container');
    const rows = container.querySelectorAll('.kcf-row');
    const errorMsg = document.getElementById('kcf-error-message');
    const entries = [];
    let hasError = false;

    for (const row of rows) {
      const code = row.querySelector('.kcf-input--code').value.trim();
      const qty = parseInt(row.querySelector('.kcf-input--qty').value, 10) || 1;

      if (!code) continue; // Skip empty rows

      const result = validateCode(code, parsedRanges);
      if (!result.valid) {
        const errEl = row.querySelector('.kcf-field__error');
        errEl.textContent = result.message;
        errEl.style.display = '';
        row.querySelector('.kcf-input--code').classList.add('kcf-input--error');
        hasError = true;
        continue;
      }

      entries.push({ code: code, qty: qty });
    }

    if (entries.length === 0) {
      errorMsg.textContent = 'Please enter at least one key code.';
      errorMsg.style.display = '';
      return null;
    }

    if (hasError) {
      errorMsg.textContent = 'Please fix the errors above.';
      errorMsg.style.display = '';
      return null;
    }

    errorMsg.style.display = 'none';

    // Build line item properties
    const properties = {};
    entries.forEach(function (entry, i) {
      properties['Key Code ' + (i + 1)] = entry.code + ' (x' + entry.qty + ')';
    });
    properties['_key_codes_data'] = JSON.stringify(entries);

    // Total quantity is sum of all row quantities
    const totalQty = entries.reduce(function (sum, e) { return sum + e.qty; }, 0);

    return { quantity: totalQty, properties: properties };
  }

  // ============================================================
  // STANDARD FORM (Type 2)
  // ============================================================

  function getStandardCartData() {
    const qtyInput = document.getElementById('kcf-standard-qty');
    const qty = parseInt(qtyInput.value, 10) || 1;
    return { quantity: qty, properties: {} };
  }

  // ============================================================
  // DROPDOWN FORM (Type 3)
  // ============================================================

  function initDropdownForm() {
    const select = document.getElementById('kcf-dropdown-select');
    if (!select || !dropdownStr) return;

    const options = dropdownStr.split(',');
    for (const opt of options) {
      const trimmed = opt.trim();
      if (!trimmed) continue;
      const el = document.createElement('option');
      el.value = trimmed;
      el.textContent = trimmed;
      select.appendChild(el);
    }
  }

  function getDropdownCartData() {
    const select = document.getElementById('kcf-dropdown-select');
    const qtyInput = document.getElementById('kcf-dropdown-qty');
    const selected = select ? select.value : '';
    const qty = parseInt(qtyInput.value, 10) || 1;

    if (!selected) {
      return null; // No option selected
    }

    return {
      quantity: qty,
      properties: {
        'Selected Option': selected,
      },
    };
  }

  // ============================================================
  // MULTI CODE FORM (Type 4)
  // ============================================================

  function initMultiCodeForm() {
    const container = document.getElementById('kcf-multi-fields');
    if (!container || !multiLabelsStr) return;

    const labels = multiLabelsStr.split(',');
    for (const label of labels) {
      const trimmed = label.trim();
      if (!trimmed) continue;
      const field = document.createElement('div');
      field.className = 'kcf-field kcf-field--multi';
      field.innerHTML =
        '<label class="kcf-field__label">Code ' + trimmed + '</label>' +
        '<input type="text" class="kcf-input kcf-input--multi" data-label="' + trimmed + '" placeholder="Enter code ' + trimmed + '" autocomplete="off">';
      container.appendChild(field);
    }
  }

  function getMultiCodeCartData() {
    const fields = document.querySelectorAll('#kcf-multi-fields .kcf-input--multi');
    const qtyInput = document.getElementById('kcf-multi-qty');
    const errorMsg = document.getElementById('kcf-multi-error');
    const qty = parseInt(qtyInput.value, 10) || 1;
    const properties = {};
    const codesData = {};
    let allFilled = true;

    for (const field of fields) {
      const label = field.dataset.label;
      const val = field.value.trim();
      if (!val) {
        allFilled = false;
        field.classList.add('kcf-input--error');
      } else {
        field.classList.remove('kcf-input--error');
        properties['Code ' + label] = val;
        codesData[label] = val;
      }
    }

    if (!allFilled) {
      if (errorMsg) {
        errorMsg.textContent = 'Please fill in all code fields.';
        errorMsg.style.display = '';
      }
      return null;
    }

    if (errorMsg) errorMsg.style.display = 'none';
    properties['_multi_codes_data'] = JSON.stringify(codesData);

    return { quantity: qty, properties: properties };
  }

  // ============================================================
  // ADD TO CART
  // ============================================================

  function addToCart(variantId, quantity, properties, blob) {
    if (blob) {
      addToCartWithFile(variantId, quantity, properties, blob);
      return;
    }

    const btn = document.getElementById('kcf-add-to-cart');
    const msg = document.getElementById('kcf-cart-message');

    btn.disabled = true;
    btn.textContent = 'Adding...';

    const body = {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: quantity,
          properties: properties,
        },
      ],
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Failed to add to cart');
        return resp.json();
      })
      .then(function () {
        onCartAddSuccess();
      })
      .catch(function (err) {
        onCartAddError();
      });
  }

  /**
   * Add to cart with a photo file via multipart/form-data to /cart/add.
   * Shopify's /cart/add (HTML endpoint) supports file uploads natively.
   */
  function addToCartWithFile(variantId, quantity, properties, blob) {
    const btn = document.getElementById('kcf-add-to-cart');
    const msg = document.getElementById('kcf-cart-message');

    btn.disabled = true;
    btn.textContent = 'Adding...';

    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', quantity);

    // Append all line item properties
    for (var key in properties) {
      if (properties.hasOwnProperty(key)) {
        fd.append('properties[' + key + ']', properties[key]);
      }
    }

    // Append the photo file — underscore prefix hides from customer
    fd.append('properties[_key_photo]', blob, 'key-photo.jpg');

    fetch('/cart/add', {
      method: 'POST',
      body: fd,
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Failed to add to cart');
        return resp.text();
      })
      .then(function () {
        // Update cart count by fetching /cart.js
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function () {
        onCartAddSuccess();
        clearPhoto();
      })
      .catch(function (err) {
        onCartAddError();
      });
  }

  function onCartAddSuccess() {
    var btn = document.getElementById('kcf-add-to-cart');
    var msg = document.getElementById('kcf-cart-message');

    msg.textContent = 'Added to basket!';
    msg.className = 'kcf-cart-message kcf-cart-message--success';
    msg.style.display = '';
    btn.textContent = 'Add to Basket';
    btn.disabled = false;

    setTimeout(function () {
      msg.style.display = 'none';
    }, 3000);
  }

  function onCartAddError() {
    var btn = document.getElementById('kcf-add-to-cart');
    var msg = document.getElementById('kcf-cart-message');

    msg.textContent = 'Error adding to basket. Please try again.';
    msg.className = 'kcf-cart-message kcf-cart-message--error';
    msg.style.display = '';
    btn.textContent = 'Add to Basket';
    btn.disabled = false;
  }

  // ============================================================
  // INITIALISE
  // ============================================================

  function init() {
    renderVolumePricing();

    if (formType === 'key_code') {
      initKeyCodeForm();
    } else if (formType === 'dropdown') {
      initDropdownForm();
    } else if (formType === 'multi_code') {
      initMultiCodeForm();
    }

    initPhotoSection();

    // Add to cart button
    const addBtn = document.getElementById('kcf-add-to-cart');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        let cartData = null;

        if (formType === 'key_code') {
          cartData = getKeyCodeCartData();
        } else if (formType === 'standard') {
          cartData = getStandardCartData();
        } else if (formType === 'dropdown') {
          cartData = getDropdownCartData();
          if (!cartData) {
            const msg = document.getElementById('kcf-cart-message');
            msg.textContent = 'Please select an option.';
            msg.className = 'kcf-cart-message kcf-cart-message--error';
            msg.style.display = '';
            return;
          }
        } else if (formType === 'multi_code') {
          cartData = getMultiCodeCartData();
        }

        if (!cartData) return;

        addToCart(variantId, cartData.quantity, cartData.properties, photoBlob);
      });
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
